import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Megaphone, Users, Phone, Mail, Plus, ShieldAlert, Lock, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const SEG_TONE = {
  consenso_marketing: "bg-emerald-100 text-emerald-700",
  richiamo_24: "bg-amber-100 text-amber-700",
  richiamo_36: "bg-red-100 text-red-700",
  attivi: "bg-sky-100 text-sky-700",
  inattivi: "bg-slate-100 text-slate-600",
};

const EMPTY = { nome: "", tipo: "promozione", canale: "email", data: "", testo: "", segmento: "consenso_marketing" };

export default function Marketing() {
  const { isAdmin } = useAuth();
  const [segmenti, setSegmenti] = useState([]);
  const [campagne, setCampagne] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [viewSeg, setViewSeg] = useState(null);
  const [segClienti, setSegClienti] = useState([]);

  const load = useCallback(async () => {
    const [s, c] = await Promise.all([api.get("/segmenti"), api.get("/campagne")]);
    setSegmenti(s.data);
    setCampagne(c.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSeg = async (key) => {
    const { data } = await api.get(`/segmenti/${key}/clienti`);
    setSegClienti(data);
    setViewSeg(key);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.nome.trim()) { toast.error("Nome campagna obbligatorio"); return; }
    setSaving(true);
    try {
      await api.post("/campagne", { ...form, data: form.data || null });
      toast.success("Campagna inviata");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-slate-900">Marketing</h1>
          <p className="text-sm text-slate-500 mt-1">Segmenti automatici e campagne WhatsApp / Email.</p>
        </div>
        {isAdmin && (
          <Button data-testid="new-campagna-btn" onClick={() => setOpen(true)} className="bg-[#1E40AF] hover:bg-[#1D4ED8] h-11">
            <Plus className="h-4 w-4 mr-2" /> Nuova campagna
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5" />
        <div className="text-sm text-amber-800">
          Le campagne usano <strong>solo</strong> nome, cognome, telefono, email, consensi e date di richiamo. <strong>Nessun</strong> dato ottico o sanitario è accessibile da quest'area.
        </div>
      </div>

      {/* Segmenti */}
      <div>
        <h2 className="font-head text-lg font-semibold text-slate-900 mb-3">Segmenti automatici</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {segmenti.map((s) => (
            <button key={s.key} data-testid={`segment-${s.key}`} onClick={() => openSeg(s.key)} className="text-left p-5 rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${SEG_TONE[s.key]}`}><Users className="h-[18px] w-[18px]" /></div>
              <div className="font-head text-2xl font-bold text-slate-900">{s.count}</div>
              <div className="text-xs text-slate-500 mt-1 leading-tight">{s.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Campagne */}
      <div>
        <h2 className="font-head text-lg font-semibold text-slate-900 mb-3">Campagne</h2>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="campagne-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Campagna</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Canale</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell">Data</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Destinatari</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {campagne.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">Nessuna campagna. {isAdmin ? "Creane una nuova." : ""}</td></tr>}
              {campagne.map((c) => (
                <tr key={c.id}>
                  <td className="px-5 py-3.5 font-medium text-slate-900">{c.nome}</td>
                  <td className="px-5 py-3.5">{c.tipo === "promozione" ? "Promozione" : "Richiamo controllo"}</td>
                  <td className="px-5 py-3.5">
                    <span className="inline-flex items-center gap-1.5">
                      {c.canale === "whatsapp" ? <Phone className="h-3.5 w-3.5 text-emerald-600" /> : <Mail className="h-3.5 w-3.5 text-sky-600" />}
                      {c.canale === "whatsapp" ? "WhatsApp" : "Email"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-slate-600">{c.data}</td>
                  <td className="px-5 py-3.5"><Badge variant="secondary">{c.destinatari}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New campaign dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="campagna-form">
          <DialogHeader><DialogTitle className="font-head">Nuova campagna</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Nome campagna</Label><Input data-testid="campagna-nome" value={form.nome} onChange={(e) => set("nome", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
                  <SelectTrigger data-testid="campagna-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="promozione">Promozione</SelectItem>
                    <SelectItem value="richiamo_controllo">Richiamo controllo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Canale</Label>
                <Select value={form.canale} onValueChange={(v) => set("canale", v)}>
                  <SelectTrigger data-testid="campagna-canale"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Segmento destinatari</Label>
              <Select value={form.segmento} onValueChange={(v) => set("segmento", v)}>
                <SelectTrigger data-testid="campagna-segmento"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {segmenti.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} ({s.count})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Data</Label><Input type="date" data-testid="campagna-data" value={form.data} onChange={(e) => set("data", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Testo messaggio</Label><Textarea data-testid="campagna-testo" value={form.testo} onChange={(e) => set("testo", e.target.value)} rows={3} placeholder="Gentile cliente, la invitiamo a…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="send-campagna-btn" onClick={submit} disabled={saving} className="bg-[#1E40AF] hover:bg-[#1D4ED8]">{saving ? "Invio…" : "Invia campagna"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View segment recipients */}
      <Dialog open={!!viewSeg} onOpenChange={(v) => !v && setViewSeg(null)}>
        <DialogContent className="max-w-lg" data-testid="segment-modal">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Eye className="h-4 w-4" /> {segmenti.find((s) => s.key === viewSeg)?.label} ({segClienti.length})</DialogTitle></DialogHeader>
          <div className="max-h-[55vh] overflow-auto custom-scroll">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-3"><Lock className="h-3.5 w-3.5" /> Vista marketing-safe: nessun dato ottico visibile.</div>
            {segClienti.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                <div>
                  <div className="text-sm font-medium text-slate-900">{c.cognome} {c.nome}</div>
                  <div className="text-xs text-slate-500">{c.telefono || "—"} · {c.email || "—"}</div>
                </div>
                <div className="flex gap-1">
                  {c.consenso_whatsapp && <Phone className="h-3.5 w-3.5 text-emerald-600" />}
                  {c.consenso_email && <Mail className="h-3.5 w-3.5 text-sky-600" />}
                </div>
              </div>
            ))}
            {segClienti.length === 0 && <div className="py-8 text-center text-sm text-slate-500">Nessun cliente in questo segmento.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
